import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import {
  ContentDiv,
  Div,
  HeaderDiv,
  HeaderIcons,
  AroundHeaderIcons,
  ScreenDiv,
  ScreenTitle,
} from '../../styles/styles';
import {
  fetchLegalPageById,
  selectLegalPageById,
  selectLegalPageLoading,
} from '../../features/legalPages/legalPagesSlice';
import { LEGAL_PAGE_META, LEGAL_PAGE_LOCALE } from '../../features/legalPages/legalPageConstants';
import { formatArticleBodyHtml } from '../../utils/articleHelpers';
import LandingHeaderSection from '../landingPage/landingHeaderSection';
import LandingFooterSection from '../landingPage/landingFooterSection';
import {
  LandingContainer,
  LandingPageRoot,
  LegalPageContentCard,
  LegalPageSection,
  LegalPageTitle,
} from '../landingPage/landingPage.styles';

const LEGAL_CONTENT_STYLES = `
  .legal-content p {
    margin: 0 0 12px 0;
  }
  .legal-content p:last-child {
    margin-bottom: 0;
  }
  .legal-content strong {
    font-weight: 600;
  }
  .legal-content p > strong:only-child {
    display: block;
    margin: 16px 0 8px 0;
  }
  .legal-content ul {
    margin: 8px 0 12px 0;
    padding-left: 24px;
    list-style: disc outside !important;
  }
  .legal-content li {
    display: list-item !important;
    margin: 4px 0;
  }
  .legal-content a {
    color: inherit;
    text-decoration: underline;
  }
`;

function LegalPageBody({ loading, contentHtml }) {
  return (
    <LegalPageContentCard>
      {loading && !contentHtml ? (
        <div style={{ fontSize: '14px' }}>Loading…</div>
      ) : (
        <div
          dangerouslySetInnerHTML={{ __html: contentHtml }}
          className="legal-content"
        />
      )}
      <style>{LEGAL_CONTENT_STYLES}</style>
    </LegalPageContentCard>
  );
}

export default function LegalPageScreen({ pageId }) {
  const meta = LEGAL_PAGE_META[pageId];
  const { t } = useTranslation(['registration']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery({ minWidth: '800px' });

  const page = useSelector(selectLegalPageById(pageId));
  const loading = useSelector(selectLegalPageLoading);
  const returnTo = location.state?.returnTo;
  const showAuthFlowChrome = Boolean(returnTo);
  const showLandingChrome = !showAuthFlowChrome;

  useEffect(() => {
    dispatch(fetchLegalPageById(pageId));
  }, [dispatch, pageId]);

  useEffect(() => {
    if (!showLandingChrome) return;

    const scrollRoot = document.getElementById('app-scroll-root');
    if (scrollRoot) {
      scrollRoot.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [pageId, showLandingChrome]);

  const { title, contentHtml } = useMemo(() => {
    const firestoreTitle = page?.exists ? (page.title?.[LEGAL_PAGE_LOCALE] || '').trim() : '';
    const firestoreContent = page?.exists
      ? (page.contentHtml?.[LEGAL_PAGE_LOCALE] || '').trim()
      : '';

    return {
      title:
        firestoreTitle ||
        t(`registration:${meta.i18nTitleKey}`, { lng: LEGAL_PAGE_LOCALE }),
      contentHtml: formatArticleBodyHtml(
        firestoreContent ||
          t(`registration:${meta.i18nContentKey}`, { lng: LEGAL_PAGE_LOCALE }),
      ),
    };
  }, [page, t, meta]);

  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
    }
  };

  if (showLandingChrome) {
    return (
      <LandingPageRoot>
        <LandingHeaderSection anchorPrefix="/" />

        <LandingContainer>
          <LegalPageSection>
            <LegalPageTitle>{title}</LegalPageTitle>
            <LegalPageBody loading={loading} contentHtml={contentHtml} />
          </LegalPageSection>
        </LandingContainer>

        <LandingFooterSection />
      </LandingPageRoot>
    );
  }

  return (
    <ScreenDiv
      noAccount={isDesktop}
      height="100%"
      justifyContent="center"
      justifyContentTablet="center"
    >
      <ContentDiv
        heightDesktop="100%"
        alignItemsMobile="center"
        borderRadius="20px"
        padding={isDesktop && '40px'}
        height="100%"
      >
        <HeaderDiv
          width={isDesktop && '400px'}
          deskHeight="auto"
          position="fixed"
          backWhite={!isDesktop}
          padding="0 0 0 20px"
        >
          <AroundHeaderIcons onClick={handleBack}>
            <HeaderIcons backIcon />
          </AroundHeaderIcons>

          <ScreenTitle fontWeight="500">{title}</ScreenTitle>

          <HeaderIcons />
        </HeaderDiv>

        <Div
          height="auto"
          alignStart
          width={isDesktop && '100%'}
          maxWidth={isDesktop && '800px'}
          padding={!isDesktop ? '50px 0 0 0' : undefined}
        >
          {isDesktop && <HeaderDiv />}

          <Div
            backLightGray
            padding="20px"
            borderRadius="8px"
            gap="15px"
            alignStart
            height="auto"
            margin="0"
            column
            justifyStart
            width="100%"
          >
            {loading && !contentHtml ? (
              <div style={{ textAlign: 'left', fontSize: '14px' }}>Loading…</div>
            ) : (
              <div
                style={{
                  textAlign: 'left',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  width: '100%',
                }}
                dangerouslySetInnerHTML={{ __html: contentHtml }}
                className="legal-content"
              />
            )}
            <style>{LEGAL_CONTENT_STYLES}</style>
          </Div>
        </Div>
      </ContentDiv>
    </ScreenDiv>
  );
}
